import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ContactForm } from '../components/forms/ContactForm';
import { DemoPopup } from '../components/forms/DemoPopup';
import {
  Users,
  Kanban,
  TrendingUp,
  Megaphone,
  PieChart,
  Code2,
  Database,
  Zap,
  BarChart3,
  Shield,
  Scale,
  ArrowRight,
  Mail,
  Clock,
} from 'lucide-react';

const stats = [
  { value: '500+', label: 'Clients Served' },
  { value: '99.9%', label: 'Uptime' },
  { value: '24/7', label: 'Support' },
  { value: '2,500+', label: 'Active Users' },
];

const solutions = [
  {
    icon: Users,
    title: 'HR Management',
    description: 'Streamline recruitment, onboarding, payroll, attendance & performance management in one place.',
    path: '/solutions/hr',
  },
  {
    icon: Kanban,
    title: 'Project Management',
    description: 'Plan, track, and deliver projects with Kanban boards, Gantt charts, and real-time collaboration.',
    path: '/solutions/project',
  },
  {
    icon: TrendingUp,
    title: 'Sales Management',
    description: 'CRM, pipeline tracking, quotations, invoicing, and analytics to close more deals faster.',
    path: '/solutions/sales',
  },
  {
    icon: Megaphone,
    title: 'Marketing Management',
    description: 'Campaign management, email automation, social media, lead scoring, and ROI tracking.',
    path: '/solutions/marketing',
  },
  {
    icon: PieChart,
    title: 'Finance Management',
    description: 'Accounting, budgeting, expense tracking, financial reports, and compliance in one dashboard.',
    path: '/solutions/finance',
  },
  {
    icon: Code2,
    title: 'Custom IT Solutions',
    description: 'Web apps, mobile apps, API integrations, and bespoke software tailored to your needs.',
    path: '/solutions/custom-it',
  },
];

const benefits = [
  { icon: Database, title: 'Centralized Data', description: 'Single source of truth' },
  { icon: Zap, title: 'Automation', description: 'Eliminate manual tasks' },
  { icon: BarChart3, title: 'Real-time Insights', description: 'Data-driven decisions' },
  { icon: Shield, title: 'Secure', description: 'Enterprise-grade security' },
  { icon: Scale, title: 'Scalable', description: 'Grows with you' },
];

export const HomePage = () => {
  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 hero-glow" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-8 animate-fade-in-up">
              <div>
                <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                  All-in-One Business Platform
                </span>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold font-heading text-white leading-tight">
                  Simplify Your{' '}
                  <span className="text-gradient">Business Operations</span>{' '}
                  with Niyanta
                </h1>
              </div>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl">
                A unified platform for HR, Projects, Sales, Marketing, Finance & Custom IT Solutions. 
                Streamline workflows, boost productivity, and scale effortlessly.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <DemoPopup 
                  trigger={
                    <Button
                      className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-medium glow-primary glow-primary-hover btn-shine"
                      data-testid="hero-book-demo-btn"
                    >
                      Book a Demo
                    </Button>
                  }
                />
                <Link to="/solutions">
                  <Button
                    variant="outline"
                    className="rounded-full px-8 py-6 text-lg font-medium border-border hover:bg-secondary/50 hover:text-primary"
                    data-testid="hero-explore-btn"
                  >
                    Explore Solutions
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right Content - Dashboard Mockup */}
            <div className="relative animate-fade-in-up stagger-2">
              <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80"
                  alt="Business Dashboard"
                  className="w-full h-auto opacity-90"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
              </div>
              {/* Floating Stats */}
              <div className="absolute -bottom-6 -left-6 glass rounded-xl p-4 animate-fade-in-up stagger-3">
                <div className="text-2xl font-bold text-primary font-heading">+24%</div>
                <div className="text-sm text-muted-foreground">Productivity</div>
              </div>
              <div className="absolute -top-4 -right-4 glass rounded-xl p-4 animate-fade-in-up stagger-4">
                <div className="text-2xl font-bold text-primary font-heading">98%</div>
                <div className="text-sm text-muted-foreground">Satisfaction</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-20 border-y border-border bg-black/30" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={stat.label}
                className={`stat-card animate-fade-in-up stagger-${index + 1}`}
                data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-24 md:py-32" data-testid="solutions-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in-up">
            <span className="text-primary text-sm font-medium uppercase tracking-widest">
              Our Solutions
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-white mt-4">
              Comprehensive Business Solutions
            </h2>
            <p className="text-muted-foreground text-lg mt-4 max-w-2xl mx-auto">
              Everything you need to run your business efficiently, all in one powerful platform.
            </p>
          </div>

          <div className="solutions-grid">
            {solutions.map((solution, index) => (
              <Link
                key={solution.title}
                to={solution.path}
                className={`glass rounded-2xl p-8 card-hover animate-fade-in-up stagger-${index + 1}`}
                data-testid={`solution-card-${solution.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="feature-icon mb-6">
                  <solution.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold font-heading text-white mb-3">
                  {solution.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {solution.description}
                </p>
                <div className="mt-6 flex items-center text-primary text-sm font-medium group-hover:gap-2 transition-all">
                  Learn More
                  <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Niyanta Section */}
      <section className="py-24 md:py-32 bg-secondary/30" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8 animate-fade-in-up">
              <div>
                <span className="text-primary text-sm font-medium uppercase tracking-widest">
                  Why Niyanta.io
                </span>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-white mt-4">
                  Built for Modern Businesses That Demand Excellence
                </h2>
              </div>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Niyanta.io isn't just another software suite – it's a complete digital transformation 
                partner. We understand that every business is unique, which is why our platform adapts 
                to your workflows, not the other way around.
              </p>
              <p className="text-muted-foreground text-lg leading-relaxed">
                From startups to enterprises, our modular approach lets you pick exactly what you need, 
                with the flexibility to scale as you grow. Experience seamless integration, intuitive 
                interfaces, and world-class support.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-muted-foreground">
                  <span className="w-2 h-2 bg-primary rounded-full" />
                  No vendor lock-in
                </li>
                <li className="flex items-center gap-3 text-muted-foreground">
                  <span className="w-2 h-2 bg-primary rounded-full" />
                  Pay for what you use
                </li>
                <li className="flex items-center gap-3 text-muted-foreground">
                  <span className="w-2 h-2 bg-primary rounded-full" />
                  Dedicated support
                </li>
              </ul>
            </div>

            <div className="relative animate-fade-in-up stagger-2">
              <div className="rounded-2xl overflow-hidden border border-white/10">
                <img
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80"
                  alt="Team Collaboration"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Strip */}
      <section className="py-16 md:py-20 border-y border-border" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={benefit.title}
                className={`text-center animate-fade-in-up stagger-${index + 1}`}
                data-testid={`benefit-${benefit.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="feature-icon mx-auto mb-4">
                  <benefit.icon className="w-6 h-6" />
                </div>
                <h4 className="text-white font-semibold font-heading">{benefit.title}</h4>
                <p className="text-muted-foreground text-sm mt-1">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 md:py-32" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Left - Info */}
            <div className="space-y-8 animate-fade-in-up">
              <div>
                <span className="text-primary text-sm font-medium uppercase tracking-widest">
                  Get in Touch
                </span>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-white mt-4">
                  Talk to Our Experts
                </h2>
              </div>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Ready to transform your business operations? Our team of experts is here to 
                understand your needs and guide you to the perfect solution.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="feature-icon flex-shrink-0">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Email us at</h4>
                    <a href="mailto:admin@niyanta.io" className="text-primary hover:underline">
                      admin@niyanta.io
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="feature-icon flex-shrink-0">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Response time</h4>
                    <p className="text-muted-foreground">Within 24 hours</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Form */}
            <div className="glass rounded-2xl p-8 animate-fade-in-up stagger-2" data-testid="home-contact-form-wrapper">
              <ContactForm formType="Home Page Inquiry" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
